import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Input_ } from "monica-alexandria";

const LocationInput = styled.div`
    display: grid;
    gap: 1rem;
`;

const LocationInputResults = styled.div`
    display: grid;
    gap: 0.6rem;
`;

const LocationInputResult = styled.button`
    display: grid;
    gap: 0.2rem;
    width: 100%;
    padding: var(--normalPads);
    border: 0;
    border-radius: var(--normalRadius);
    color: ${p => p.theme.color};
    background: ${p => p.theme.low};
    text-align: left;
    cursor: pointer;
`;

/**
 * Checks that the location search result contains usable coordinates.
 */
const read_latlng_is_valid = (latlng) => Number.isFinite(latlng?.lat) && Number.isFinite(latlng?.lng);

/**
 * Reads the Nominatim search URL for the typed location query.
 */
const read_location_search_url = (value) => {
    const query = encodeURIComponent(value.trim());

    return `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=5&q=${query}`;
};

/**
 * Normalizes one Nominatim result for the visible location search list.
 */
const read_search_result = (result) => {
    const latlng = {
        lat: Number(result?.lat),
        lng: Number(result?.lon)
    };

    return {
        id: result?.place_id,
        label: result?.display_name,
        type: result?.type,
        latlng
    };
};

/**
 * Renders a Nominatim location input and only saves selected search results.
 */
export const Location_Input = (props) => {
    const selectedValueRef = useRef(props?.value || "");
    const skipValueSyncRef = useRef(false);
    const [searchValue, setSearchValue] = useState(props?.value || "");
    const [searchResults, setSearchResults] = useState([]);

    useEffect(() => {
        if (skipValueSyncRef.current) {
            skipValueSyncRef.current = false;
            return;
        }

        selectedValueRef.current = props?.value || "";
        setSearchValue(props?.value || "");
    }, [props?.value]);

    useEffect(() => {
        const cleanSearchValue = `${searchValue || ""}`.trim();

        if (!cleanSearchValue || cleanSearchValue === selectedValueRef.current) {
            setSearchResults([]);
            return;
        }

        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => {
            fetch(read_location_search_url(cleanSearchValue), {
                signal: controller.signal
            })
                .then((response) => response.json())
                .then((results) => setSearchResults((results || []).map(read_search_result).filter((result) => read_latlng_is_valid(result?.latlng))))
                .catch(() => setSearchResults([]));
        }, 350);

        return () => {
            controller.abort();
            window.clearTimeout(timeoutId);
        };
    }, [searchValue]);

    /**
     * Keeps search text local so only picked results reach the parent form.
     */
    const change_search_value = (event) => {
        selectedValueRef.current = "";
        skipValueSyncRef.current = true;
        setSearchValue(event.target.value);
        props?.onChange({
            target: {
                value: ""
            }
        });
    };

    /**
     * Stores one selected location result into the parent form value.
     */
    const select_search_result = (result) => {
        selectedValueRef.current = result?.label;
        setSearchValue(result?.label);
        setSearchResults([]);
        props?.onChange({
            target: {
                value: result?.label
            }
        });
    };

    /**
     * Adds only values that came from the search result list.
     */
    const add_selected_location = () => {
        if (!props?.value || props?.value !== selectedValueRef.current) {
            return;
        }

        props?.onAdd();
    };

    return (
        <LocationInput>
            <Input_
                placeholder={props?.placeholder}
                value={searchValue}
                list={props?.list}
                icon_ListItem={props?.icon_ListItem}
                add={props?.add}
                onChange={change_search_value}
                onAdd={add_selected_location}
                onDelete={props?.onDelete}
            />
            {searchResults.length ? (
                <LocationInputResults>
                    {searchResults.map((result, resultIndex) => (
                        <LocationInputResult
                            key={result?.id || resultIndex}
                            type="button"
                            onClick={() => select_search_result(result)}
                        >
                            <h5>{result?.label}</h5>
                            <h6>{result?.type}</h6>
                        </LocationInputResult>
                    ))}
                </LocationInputResults>
            ) : null}
        </LocationInput>
    );
};
